import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, selectAtom } from 'jotai/utils';

import { recentHistoryBunja } from '@flipster/features/src/recent-history/state';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { symbolFavoritesBunja } from '@flipster/features/src/shared/favorite-symbol/state';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import {
  TradFiCategory,
  getAvailableTradFiCategories,
  getTradFiCategorySymbols,
  isTradFiInstrument,
} from '@flipster/features/src/shared/rwa';
import { rawInstrumentsKeyBySymbolAtom, snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { symbolsQueryAtom } from '@flipster/features/src/shared/symbol/symbols';
import { marketsBunja } from '@flipster/r230522/original/market';
import { spotMarketsBunja } from '@flipster/r230522/original/spot-market';
import { getTickerDisplayPriceForSymbol } from '@flipster/r230522/original/ticker-price';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { instrumentFuzzySearch } from '@flipster/utils/fuzzy';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';
import { sortingHelper } from '@flipster/utils/sort';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import { symbolLeftBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol-info/state';

import { getDefaultTab, getSearchEntryTab, resolveTabOnSearchModeChange, type TabType } from './utils';

const tabOrderDefault = ['favorites', 'tradFi', 'perpetual', 'spot'] as const satisfies TabType[];
const tabOrderSpotOnly = ['favorites', 'spot'] as const satisfies TabType[];
const tabOrderSpotOnlyLoggedOut = ['spot'] as const satisfies TabType[];
const tabOrderSearching = ['perpetual', 'tradFi', 'spot'] as const satisfies TabType[];

export type { TradFiCategory };

interface SortType {
  key: 'turnover24h' | 'displayPrice' | 'priceChangePct24h' | 'leverage';
  order: 'asc' | 'desc';
}
type TickerSortKey = Exclude<SortType['key'], 'leverage'>;

const getDefaultSortForTab = (tab: TabType): SortType | undefined =>
  tab === 'tradFi' || tab === 'perpetual' ? { key: 'turnover24h', order: 'desc' } : undefined;

export const marketListBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { favoritesAtom, toggleFavorite } = bunja.use(symbolFavoritesBunja);
  const { isSpotOnlyRegulationAtom, shouldFilterToVaraSymbolsAtom } = bunja.use(userRegulationBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { spotMarketsAtom } = bunja.use(spotMarketsBunja);
  const { isSpotPath } = bunja.use(symbolLeftBunja);
  const { isTradFiInstrumentAtom } = bunja.use(tradeSymbolBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);

  const searchAtom = atom('');
  const tradFiCategoryAtom = atom<TradFiCategory>('all');
  const availableTradFiCategoriesAtom = atom((get) => getAvailableTradFiCategories(get(symbolsQueryAtom).data));
  const defaultTabAtom = atom<TabType>((get) =>
    getDefaultTab({
      isSpotOnly: get(isSpotOnlyRegulationAtom),
      isSpotPath,
      isTradFiInstrument: get(isTradFiInstrumentAtom),
    }),
  );
  const searchEntryTabAtom = atom<TabType>((get) =>
    getSearchEntryTab({ isSpotOnly: get(isSpotOnlyRegulationAtom), isSpotPath }),
  );
  const currentTabAtom = atomWithDefault<TabType>((get) => get(defaultTabAtom));
  const sortAtom = atomWithDefault<SortType | undefined>((get) => getDefaultSortForTab(get(currentTabAtom)));
  const recentFavoritesAtom = atomWithDefault((get) => get(favoritesAtom));

  const instrumentAtomFamily = atomFamily((symbol: string) =>
    atom((get) => {
      const instrument = get(rawInstrumentsKeyBySymbolAtom)?.[symbol];
      const productType = instrument?.productType === 'PRODUCT_TYPE_SPOT' ? 'spot' : 'perpetual';
      return {
        displayName: instrument?.displayName,
        displayShortName: instrument?.displayShortName,
        productType: productType as 'perpetual' | 'spot',
        pricePrecision: instrument?.pricePrecision || 2,
        isTradFi: isTradFiInstrument(instrument),
        leverage: get(instrumentsKeyBySymbolAtom)?.[symbol]?.maxLeverageHardLimit,
        maxInitLeverageIsolatedMargin: get(instrumentsKeyBySymbolAtom)?.[symbol]?.maxInitLeverageIsolatedMargin,
      };
    }),
  );

  const tickerAtomFamily = atomFamily((symbol: string) =>
    selectAtom(
      tickersAtom,
      (tickerMap) => {
        const ticker = tickerMap[symbol];
        return {
          turnover24h: ticker?.turnover24h,
          displayPrice: getTickerDisplayPriceForSymbol(symbol, ticker),
          priceChangePct24h: ticker?.priceChangePct24h,
        };
      },
      (a, b) =>
        a?.turnover24h === b?.turnover24h &&
        a?.displayPrice === b?.displayPrice &&
        a?.priceChangePct24h === b?.priceChangePct24h,
    ),
  );

  const tabsAtom = atom((get) => {
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const isSearching = get(searchAtom).trim().length > 0;

    const tabs: readonly TabType[] = isSpotOnly
      ? get(isLoggedInAtom)
        ? tabOrderSpotOnly
        : tabOrderSpotOnlyLoggedOut
      : tabOrderDefault;
    if (!isSearching) return tabs;
    return isSpotOnly ? tabs.filter((tab) => tab !== 'favorites') : tabOrderSearching;
  });

  const isLoadingAtom = atom((get) => {
    const symbols = get(symbolsQueryAtom);
    return !symbols.data;
  });

  const allSymbolsAtom = atom<string[]>((get) => {
    const symbols = get(symbolsQueryAtom);
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const instruments = get(rawInstrumentsKeyBySymbolAtom);

    if (!symbols.data || !instruments) return [];
    return symbols.data.data.marketCap.filter((symbol) => {
      if (isSpotOnly) return instruments[symbol]?.isSpot;
      return true;
    });
  });

  const tabFilteredSymbolsAtom = atom((get) => {
    const tab = get(currentTabAtom);
    const allSymbols = get(allSymbolsAtom);
    const symbols = get(symbolsQueryAtom);
    const recentFavorites = get(recentFavoritesAtom);
    const instruments = get(rawInstrumentsKeyBySymbolAtom);
    const showVaraSymbolsOnly = get(isSpotOnlyRegulationAtom) && get(shouldFilterToVaraSymbolsAtom);

    switch (tab) {
      case 'perpetual':
        return allSymbols.filter((symbol) => instruments?.[symbol]?.isFutures);
      case 'spot':
        return allSymbols.filter((symbol) => {
          const inst = instruments?.[symbol];
          if (!inst?.isSpot) return false;
          if (showVaraSymbolsOnly) return inst.regulationStatus === 'VARA_REGULATED';
          return inst.regulationStatus !== 'VARA_REGULATED';
        });
      case 'favorites':
        return allSymbols.filter((symbol) => recentFavorites.has(symbol));
      case 'tradFi':
        if (get(searchAtom).trim()) return getTradFiCategorySymbols(symbols.data, 'all');
        return getTradFiCategorySymbols(symbols.data, get(tradFiCategoryAtom));
      default:
        return [];
    }
  });

  const fuzzySortedSymbolsAtom = atom((get) => {
    const search = get(searchAtom).trim();
    const tabFilteredSymbols = get(tabFilteredSymbolsAtom);
    const instrumentMap = get(rawInstrumentsKeyBySymbolAtom);
    if (!search) return tabFilteredSymbols;
    return instrumentFuzzySearch(search, tabFilteredSymbols, instrumentMap);
  });

  const tickersAtom = atom((get) => {
    const markets = get(marketsAtom);
    const spotMarkets = get(spotMarketsAtom);
    return { ...markets, ...spotMarkets };
  });

  const displayedSymbolsAtom = atom((get) => {
    const search = get(searchAtom).trim();
    const sort = get(sortAtom);
    const fuzzySortedSymbols = get(fuzzySortedSymbolsAtom);

    if (search || !sort) return fuzzySortedSymbols;

    if (sort.key === 'leverage') {
      const instruments = get(instrumentsKeyBySymbolAtom);
      return fuzzySortedSymbols.toSorted((a, b) =>
        sortingHelper(instruments?.[a]?.maxLeverageHardLimit ?? 0, instruments?.[b]?.maxLeverageHardLimit ?? 0, {
          type: 'number',
          order: sort.order,
        }),
      );
    }

    const tickers = get(tickersAtom);
    const tickerSortKey: TickerSortKey = sort.key;
    return fuzzySortedSymbols.toSorted((a, b) => {
      const valueA =
        tickerSortKey === 'displayPrice'
          ? getTickerDisplayPriceForSymbol(a, tickers?.[a])
          : tickers?.[a]?.[tickerSortKey];
      const valueB =
        tickerSortKey === 'displayPrice'
          ? getTickerDisplayPriceForSymbol(b, tickers?.[b])
          : tickers?.[b]?.[tickerSortKey];

      if (!valueA && !valueB) return 0;
      if (!valueA) return 1;
      if (!valueB) return -1;

      return sortingHelper(valueA, valueB, { type: 'bignumber', order: sort.order });
    });
  });

  const closestSymbolAtom = atom((get) => {
    const search = get(searchAtom).trim();
    const displayedSymbols = get(displayedSymbolsAtom);
    if (!search || displayedSymbols.length === 0) return null;
    return displayedSymbols[0];
  });

  bunja.effect(() => {
    const clearSort = () => store.set(sortAtom, getDefaultSortForTab(store.get(currentTabAtom)));
    const clearTradFiCategory = () => store.set(tradFiCategoryAtom, 'all');
    let hadSearch = store.get(searchAtom).trim().length > 0;
    const syncTabWithSearchMode = () => {
      const hasSearch = store.get(searchAtom).trim().length > 0;
      const nextTab = resolveTabOnSearchModeChange({
        hasSearch,
        hadSearch,
        currentTab: store.get(currentTabAtom),
        searchEntryTab: store.get(searchEntryTabAtom),
        defaultTab: store.get(defaultTabAtom),
      });
      hadSearch = hasSearch;
      if (nextTab !== null) store.set(currentTabAtom, nextTab);
    };

    const unsubClearSort = store.sub(currentTabAtom, clearSort);
    const unsubClearTradFiCategory = store.sub(currentTabAtom, clearTradFiCategory);
    const unsubRefreshFavorites = store.sub(currentTabAtom, refreshRecentFavorites);
    const unsubSyncTabWithSearchMode = store.sub(searchAtom, syncTabWithSearchMode);
    return () => {
      unsubClearSort();
      unsubClearTradFiCategory();
      unsubRefreshFavorites();
      unsubSyncTabWithSearchMode();
    };
  });

  const refreshRecentFavorites = () => store.set(recentFavoritesAtom, store.get(favoritesAtom));

  return {
    searchAtom,
    sortAtom,
    tradFiCategoryAtom,
    availableTradFiCategoriesAtom,
    currentTabAtom,
    tabsAtom,
    isLoadingAtom,
    displayedSymbolsAtom,
    closestSymbolAtom,
    favoritesAtom,
    tickerAtomFamily,
    instrumentAtomFamily,
    toggleFavorite,
    refreshRecentFavorites,
  };
});
marketListBunja.debugLabel = 'marketListBunja';

export const trackMarketListBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { currentTabAtom, searchAtom, instrumentAtomFamily, tickerAtomFamily, displayedSymbolsAtom } =
    bunja.use(marketListBunja);
  const { addRecentSearchHistory } = bunja.use(recentHistoryBunja);

  // TODO logging 용 값 앞으로 변경해야할때 이야기해서 value 자체를 보내도록 이야기할 것
  // 다국어로 label 값을 사용하는 형태는 지양 필요
  const searchType = {
    favorites: 'Favorites',
    perpetual: 'Perpetual',
    tradFi: 'TradFi',
    spot: 'Spot',
  } as const satisfies Record<TabType, string>;

  function openSearchList(): void {
    segment.track(TradeEvent.SearchSelectionsView);
  }

  function clickSearchInput(): void {
    store.set(orderAttributionAtom, OrderAttributionMapper.trade.search);
  }

  function clickTab(tab: TabType, order: number) {
    segment.track(TradeEvent.SearchTypeClick, {
      search_type: searchType[tab],
      list_order: order,
    });
  }

  function clickSymbol(carouselType: 'Search list' | 'Recent history', symbol: string, order: number): void {
    const tab = store.get(currentTabAtom);
    const search = store.get(searchAtom);
    const instrument = store.get(instrumentAtomFamily(symbol));
    const ticker = store.get(tickerAtomFamily(symbol));

    addRecentSearchHistory(symbol);
    store.set(searchAtom, '');
    segment.track(TradeEvent.TradeCryptoClick, {
      symbol,
      carousel_type: carouselType,
      list_order: order,
      trade_type: instrument.productType,
      search_type: searchType[tab],
      search_word: search,
      price_change_percentage: map(ticker.priceChangePct24h, Number) ?? null,
    });
  }

  const emptySearchResultWordAtom = atom((get) => {
    const search = get(searchAtom).trim();
    if (search.length === 0) return null;
    const symbols = get(displayedSymbolsAtom);
    if (symbols.length > 0) return null;
    return search;
  });

  bunja.effect(() => {
    function trackEmptySearchResult() {
      const search = store.get(emptySearchResultWordAtom);
      if (search === null) return;
      segment.track(TradeEvent.EmptySearchResultPageView, {
        search_type: store.get(currentTabAtom),
        search_word: search,
      });
    }

    const unsubEmptySearchResultWordAtom = store.sub(emptySearchResultWordAtom, trackEmptySearchResult);
    return () => {
      unsubEmptySearchResultWordAtom();
    };
  });

  return { openSearchList, clickSearchInput, clickTab, clickSymbol };
});
trackMarketListBunja.debugLabel = 'trackMarketListBunja';
